import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import Header from '../Header';
import MaerskButton from '../common/MaerskButton';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import TestSystemMessage from './TestSystemMessage';
import DeployToCdt from './DeployToCdt';
import { DeploySummary, DeployChanges } from './DeployChanges';
import { McButton } from '@maersk-global/mds-react-wrapper/components-core/mc-button';
import { McCodePreview } from '@maersk-global/mds-react-wrapper/components-core/mc-code-preview';
import { McDrawer } from '@maersk-global/mds-react-wrapper/components-core/mc-drawer';
import { McIcon } from '@maersk-global/mds-react-wrapper/components-core/mc-icon';
import { McInput } from '@maersk-global/mds-react-wrapper/components-core/mc-input';
import { McModal } from '@maersk-global/mds-react-wrapper/components-core/mc-modal';
import { McNotification } from '@maersk-global/mds-react-wrapper/components-core/mc-notification';
import { McTag } from '@maersk-global/mds-react-wrapper/components-core/mc-tag';
import { McTextarea } from '@maersk-global/mds-react-wrapper/components-core/mc-textarea';
import { COLORS, SPACING, BORDER_RADIUS, SHADOWS, FONT_FAMILY, FONT_SIZES } from '../../constants/theme';
import api from '../../services/api';

const SystemMessageEditor = ({ message, isCreating, onClose, onSaveSuccess }) => {
  const { accounts } = useMsal();
  const userName = accounts[0]?.name || accounts[0]?.username || 'anonymous';
  const userEmail = accounts[0]?.username || '';
  const [formData, setFormData] = useState({
    name: '',
    system_message: '',
    response_format: {},
    created_by: ''
  });
  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [jsonText, setJsonText] = useState('{}');
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [shouldBlink, setShouldBlink] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(null);
  const [viewingVersion, setViewingVersion] = useState(null);

  useEffect(() => {
    if (message) {
      // Parse response_format if it's a string, otherwise use it as-is
      let parsedFormat = {};
      try {
        if (typeof message.response_format === 'string') {
          parsedFormat = JSON.parse(message.response_format);
        } else if (message.response_format && typeof message.response_format === 'object') {
          parsedFormat = message.response_format;
        }
      } catch (e) {
        console.error('Failed to parse response_format:', e);
        parsedFormat = {};
      }
      const initialData = {
        name: message.name || '',
        system_message: message.system_message || '',
        response_format: parsedFormat,
        created_by: message.created_by || ''
      };
      setFormData(initialData);
      setOriginalData(initialData);
      setJsonText(JSON.stringify(parsedFormat, null, 2));
      // Fire event to show Neu's thought bubble
      window.dispatchEvent(new CustomEvent('systemMessageEditorOpen'));
    } else {
      const initialData = {
        name: '',
        system_message: '',
        response_format: {},
        created_by: ''
      };
      setFormData(initialData);
      setOriginalData(initialData);
      setJsonText('{}');
      // Fire event to hide Neu's thought bubble
      window.dispatchEvent(new CustomEvent('systemMessageEditorClose'));
    }
  }, [message]);

  // Trigger blink animation when hasChanges becomes true
  useEffect(() => {
    if (hasChanges && !saving) {
      setShouldBlink(true);
      const timer = setTimeout(() => {
        setShouldBlink(false);
      }, 2000); // Blink for 2 seconds
      return () => clearTimeout(timer);
    }
  }, [hasChanges, saving]);

  useEffect(() => {
    if (originalData) {
      const changed = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasChanges(changed);
    }
  }, [formData, originalData]);

  // Expose current editor content to window for Neu assistant
  useEffect(() => {
    window.systemMessageEditorContent = {
      system_message: formData.system_message,
      response_format: formData.response_format
    };
    
    return () => {
      window.systemMessageEditorContent = null;
    };
  }, [formData.system_message, formData.response_format]);

  // Listen for updates from Neu assistant
  useEffect(() => {
    const handleUpdate = (event) => {
      const { prompt, jsonSchema, markdown } = event.detail || {};
      if (markdown) {
        // If markdown is present, strip formatting and update system_message as plain text
        const plain = markdown.replace(/\n/g, '\n').replace(/\*\*|\*/g, '');
        setFormData(prev => ({ ...prev, system_message: plain }));
      } else if (prompt) {
        setFormData(prev => ({ ...prev, system_message: prompt }));
      }
      if (jsonSchema) {
        setFormData(prev => ({ ...prev, response_format: jsonSchema }));
        setJsonText(JSON.stringify(jsonSchema, null, 2));
      }
    };
    window.addEventListener('updateSystemMessage', handleUpdate);
    return () => {
      window.removeEventListener('updateSystemMessage', handleUpdate);
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleJsonEdit = (edit) => {
    try {
      // The edit object contains the updated JSON data
      const newData = edit.src;
      // Create a deep copy to ensure React detects the change
      const updatedData = JSON.parse(JSON.stringify(newData));
      
      setFormData(prev => ({
        ...prev,
        response_format: updatedData,
        // Add a timestamp to force state change detection
        _lastModified: Date.now()
      }));
      setJsonText(JSON.stringify(updatedData, null, 2));
      setJsonError('');
      
      // Manually trigger change detection
      setHasChanges(true);
    } catch (e) {
      console.error('JSON edit error:', e);
      setJsonError('Invalid JSON');
    }
  };

  const handleCopyJson = async () => {
    try {
      const jsonString = JSON.stringify(formData.response_format, null, 2);
      await navigator.clipboard.writeText(jsonString);
      // Optional: Show a success message
    } catch (e) {
      console.error('Failed to copy JSON:', e);
      setJsonError('Failed to copy to clipboard');
    }
  };

  const handlePasteJson = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = JSON.parse(text);
      const updatedData = JSON.parse(JSON.stringify(parsed));
      
      setFormData(prev => ({
        ...prev,
        response_format: updatedData,
        _lastModified: Date.now()
      }));
      setJsonText(JSON.stringify(updatedData, null, 2));
      setJsonError('');
      setHasChanges(true);
    } catch (e) {
      console.error('Failed to paste JSON:', e);
      setJsonError('Invalid JSON in clipboard');
    }
  };

  const openHistory = async () => {
    setShowHistory(true);
    setLoadingVersions(true);
    try {
      const res = await api.get(`/system-messages/${message.id}/versions`);
      setVersions(res.data.versions || []);
    } catch (e) {
      console.error('Failed to load versions', e);
      setVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRestoreVersion = async (version) => {
    setRestoringVersion(version.id);
    try {
      const res = await api.get(`/system-messages/${message.id}/versions/${version.id}`);
      const v = res.data;
      let parsedFormat = {};
      try {
        parsedFormat = typeof v.response_format === 'string' ? JSON.parse(v.response_format) : v.response_format;
      } catch (_) {}
      setFormData(prev => ({ ...prev, system_message: v.system_message, response_format: parsedFormat }));
      setJsonText(JSON.stringify(parsedFormat, null, 2));
      setHasChanges(true);
      setShowHistory(false);
    } catch (e) {
      console.error('Failed to restore version', e);
    } finally {
      setRestoringVersion(null);
    }
  };

  const openViewVersion = async (version) => {
    try {
      const res = await api.get(`/system-messages/${message.id}/versions/${version.id}`);
      const v = res.data;
      let parsedFormat = {};
      try {
        parsedFormat = typeof v.response_format === 'string' ? JSON.parse(v.response_format) : v.response_format;
      } catch (_) {}
      setViewingVersion({ ...v, response_format: parsedFormat });
    } catch (e) {
      console.error('Failed to load version', e);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!formData.system_message.trim()) {
      setError('System message is required');
      return false;
    }
    if (Object.keys(formData.response_format).length === 0) {
      setError('Response format must have at least one field');
      return false;
    }
    if (jsonError) {
      setError('Please fix JSON errors before saving');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    setError('');
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const url = isCreating
        ? '/system-messages'
        : `/system-messages/${message.id}`;

      const method = isCreating ? 'post' : 'put';

      const response = await api[method](url, { ...formData, ...(isCreating && userEmail ? { created_by: userEmail } : {}), saved_by: userName });
      const data = response.data;

      onSaveSuccess();
    } catch (err) {
      console.error('Error saving:', err);
      const data = err.response?.data;
      setError((data && data.error) || 'Failed to save instruction');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.05); }
          }
        `}
      </style>
      <div style={{
        minHeight: '100vh',
        backgroundColor: COLORS.backgroundLight,
        display: 'flex',
        flexDirection: 'column'
      }}>
      <Header />

      {/* Top Bar with Actions */}
      <div style={{
        backgroundColor: COLORS.white,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: `${SPACING.lg}px ${SPACING.xl}px`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: SHADOWS.sm
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
          <McButton
            label="Back"
            hiddenlabel
            icon="arrow-left"
            appearance="neutral"
            variant="plain"
            click={onClose}
          />
          <div>
            <h2 className="mds-headline--x-small" style={{ margin: 0, color: COLORS.text }}>
              {isCreating ? 'Create Instruction' : `Edit: ${message?.name}`}
            </h2>
          </div>
        </div>

        <div style={{ display: 'flex', gap: SPACING.md, alignItems: 'center' }}>
          {!isCreating && (
            <McButton
              icon="clock"
              appearance="neutral"
              variant="outlined"
              arialabel="Version history"
              click={openHistory}
            >
              History
            </McButton>
          )}
          <div style={{
            animation: shouldBlink ? 'blink 0.5s ease-in-out 4' : 'none'
          }}>
            <MaerskButton
              variant="outline"
              onClick={() => setShowTestModal(true)}
              disabled={!hasChanges || !formData.system_message || Object.keys(formData.response_format).length === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACING.xs
              }}
            >
              Try Your Changes
            </MaerskButton>
          </div>
          <MaerskButton
            variant="outline"
            onClick={() => setShowDeployModal(true)}
            disabled={isCreating || !formData.system_message.trim() || Object.keys(formData.response_format).length === 0 || !!jsonError}
          >
            Deploy to CDT
          </MaerskButton>
          <MaerskButton
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </MaerskButton>
          <MaerskButton
            onClick={handleSave}
            disabled={!hasChanges || saving || !!jsonError}
            loading={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </MaerskButton>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{ margin: `${SPACING.md}px ${SPACING.xl}px` }}>
          <McNotification appearance="error" fit="small">{error}</McNotification>
        </div>
      )}

      {/* Main Content - Two Column Layout */}
      <div style={{
        flex: 1,
        padding: SPACING.xl,
        display: 'flex',
        gap: SPACING.xl,
        alignItems: 'stretch',
        maxWidth: '100%'
      }}>
        {/* Left Column - System Message (75%) */}
        <div style={{
          flex: '0 0 75%',
          backgroundColor: COLORS.white,
          borderRadius: BORDER_RADIUS.lg,
          boxShadow: SHADOWS.lg,
          border: `1px solid ${COLORS.border}`,
          padding: SPACING.xl,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '800px'
        }}>
          {/* Name Field */}
          <div style={{ marginBottom: SPACING.lg }}>
            <McInput
              label="Name"
              required
              value={formData.name}
              input={(e) => handleInputChange('name', e.target.value)}
              disabled={!isCreating}
              placeholder="e.g., Invoice Parser, BL Extractor"
              hint={isCreating ? undefined : 'Name cannot be changed after creation'}
            />
          </div>

          {/* System Message Field - Takes remaining space */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <McTextarea
              label="System Message"
              required
              rows={30}
              value={formData.system_message}
              input={(e) => handleInputChange('system_message', e.target.value)}
              placeholder="Enter the system message that defines the AI's behavior and instructions..."
            />
            <p className="mds-text--x-small-normal" style={{
              margin: `${SPACING.xs}px 0 0 0`,
              color: COLORS.textSecondary,
              textAlign: 'right'
            }}>
              {formData.system_message.length} characters
            </p>
          </div>
        </div>

        {/* Right Column - Response Format (25%) */}
        <div style={{
          flex: '0 0 24%',
          backgroundColor: COLORS.white,
          borderRadius: BORDER_RADIUS.lg,
          boxShadow: SHADOWS.lg,
          border: `1px solid ${COLORS.border}`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '800px'
        }}>
          {/* Header */}
          <div style={{
            padding: SPACING.lg,
            borderBottom: `1px solid ${COLORS.borderLight}`,
            position: 'sticky',
            top: 0,
            backgroundColor: COLORS.white,
            zIndex: 10,
            borderTopLeftRadius: BORDER_RADIUS.lg,
            borderTopRightRadius: BORDER_RADIUS.lg,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 className="mds-text--medium-bold" style={{ margin: 0, color: COLORS.text }}>
              Response Format <span style={{ color: COLORS.error }}>*</span>
            </h3>

            {/* Copy/Paste Actions */}
            <div style={{
              display: 'flex',
              gap: SPACING.xs
            }}>
              <McButton fit="small" appearance="neutral" variant="outlined" click={handleCopyJson}>
                Copy
              </McButton>
              <McButton fit="small" appearance="neutral" variant="outlined" click={handlePasteJson}>
                Paste
              </McButton>
            </div>
          </div>

          {/* Content Area - Scrollable */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: SPACING.lg,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* JSON Editor: MDS has no editable JSON tree, so this stays on react18-json-view. */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: COLORS.white,
              padding: SPACING.md,
              borderRadius: BORDER_RADIUS.md,
              border: `1px solid ${jsonError ? COLORS.errorBorder : COLORS.border}`,
              overflow: 'auto'
            }}>
              <JsonView
                src={formData.response_format || {}}
                theme="default"
                dark={false}
                editable={true}
                onEdit={handleJsonEdit}
                onAdd={handleJsonEdit}
                onDelete={handleJsonEdit}
                collapsed={false}
                collapseStringsAfterLength={100}
                enableClipboard={true}
                displayDataTypes={false}
                quotesOnKeys={false}
                style={{
                  fontFamily: FONT_FAMILY,
                  fontSize: FONT_SIZES.md,
                  lineHeight: 1.6,
                  backgroundColor: COLORS.white,
                  padding: 0
                }}
              />
              {jsonError && (
                <p className="mds-text--x-small-normal" style={{
                  margin: `${SPACING.xs}px 0 0 0`,
                  color: COLORS.error,
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING.xs
                }}>
                  <McIcon icon="exclamation-triangle" size="20" color={COLORS.error} />
                  {jsonError}
                </p>
              )}
            </div>
          </div>

          {/* Helper Text at Bottom */}
          <div style={{
            padding: SPACING.md,
            borderTop: `1px solid ${COLORS.borderLight}`,
            backgroundColor: COLORS.backgroundLight,
            borderBottomLeftRadius: BORDER_RADIUS.lg,
            borderBottomRightRadius: BORDER_RADIUS.lg
          }}>
            <p className="mds-text--x-small-normal" style={{
              margin: 0,
              color: COLORS.textSecondary
            }}>
              Edit JSON structure directly. Click on fields to add, edit, or delete properties.
            </p>
          </div>
        </div>
      </div>
      
      {/* Test Modal */}
      {showTestModal && (
        <TestSystemMessage
          systemMessage={formData.system_message}
          responseFormat={formData.response_format}
          onClose={() => setShowTestModal(false)}
        />
      )}

      {/* Deploy to CDT Modal */}
      {showDeployModal && (
        <DeployToCdt
          messageId={message?.id}
          messageName={formData.name}
          systemMessage={formData.system_message}
          responseFormat={formData.response_format}
          deployedBy={userName}
          onClose={() => setShowDeployModal(false)}
        />
      )}

      {/* Version History Drawer */}
      {showHistory && (
        <McDrawer open position="right" customsize="380px" nopadding closed={() => setShowHistory(false)}>
          <span slot="heading">Version History</span>
          <div style={{ padding: `${SPACING.sm}px ${SPACING.xl}px`, backgroundColor: COLORS.infoBg, borderBottom: `1px solid ${COLORS.border}` }}>
            <p className="mds-text--x-small-normal" style={{ margin: 0, color: COLORS.infoText }}>
              A snapshot is saved each time you save the message, and each Deploy to CDT is logged with who deployed it and where. Restoring fills the editor — you still need to save to apply.
            </p>
          </div>

          {/* Version list */}
          <div>
            {loadingVersions ? (
              <div className="mds-text--small-normal" style={{ padding: SPACING.xl, textAlign: 'center', color: COLORS.textSecondary }}>
                Loading versions…
              </div>
            ) : versions.length === 0 ? (
              <div className="mds-text--small-normal" style={{ padding: SPACING.xl, textAlign: 'center', color: COLORS.textSecondary }}>
                No saved versions yet. Versions are created each time you save.
              </div>
            ) : (
              versions.map((v, idx) => (
                <div key={v.id} style={{
                  padding: `${SPACING.md}px ${SPACING.xl}px`,
                  borderBottom: `1px solid ${COLORS.borderLight}`,
                  display: 'flex', flexDirection: 'column', gap: SPACING.xs,
                  backgroundColor: idx === 0 ? COLORS.backgroundLight : COLORS.white,
                  transition: 'background 0.15s'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
                      <McTag appearance="neutral-inverse" fit="small">v{v.version_number}</McTag>
                      {idx === 0 && (
                        <McTag appearance="info" fit="small">Latest</McTag>
                      )}
                      {v.event_type === 'cdt_deploy' && (
                        <McTag appearance="success" fit="small">Deployed to CDT</McTag>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: SPACING.xs }}>
                      <McButton fit="small" appearance="neutral" variant="outlined" click={() => openViewVersion(v)}>
                        View
                      </McButton>
                      <McButton
                        fit="small"
                        appearance="neutral"
                        variant="outlined"
                        disabled={restoringVersion === v.id}
                        click={() => handleRestoreVersion(v)}
                      >
                        {restoringVersion === v.id ? 'Restoring…' : 'Restore'}
                      </McButton>
                    </div>
                  </div>
                  <div className="mds-text--x-small-normal" style={{ color: COLORS.textSecondary }}>
                    {new Date(v.saved_at).toLocaleString()} · {v.saved_by || 'unknown'}
                  </div>
                  {v.event_type === 'cdt_deploy' && v.deployment && (
                    <DeploySummary deployment={v.deployment} deployedBy={v.saved_by} />
                  )}
                  {v.preview && (
                    <div className="mds-text--x-small-normal" style={{
                      color: COLORS.textSecondary,
                      backgroundColor: COLORS.backgroundLight,
                      border: `1px solid ${COLORS.borderLight}`,
                      borderRadius: BORDER_RADIUS.sm,
                      padding: `${SPACING.xs}px ${SPACING.sm}px`,
                      maxHeight: 60, overflow: 'hidden',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                    }}>
                      {v.preview}{v.preview.length >= 200 ? '…' : ''}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </McDrawer>
      )}
      {/* Version View Modal */}
      {viewingVersion && (
        <McModal
          open
          heading={`v${viewingVersion.version_number} · ${message?.name || ''}`}
          width="700px"
          closed={() => setViewingVersion(null)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.lg }}>
            <div className="mds-text--x-small-normal" style={{ color: COLORS.textSecondary }}>
              {viewingVersion.event_type === 'cdt_deploy' ? 'Deployed to CDT' : 'Saved'} {new Date(viewingVersion.saved_at).toLocaleString()} by {viewingVersion.saved_by || 'unknown'}
            </div>

            {viewingVersion.event_type === 'cdt_deploy' && <DeployChanges deployment={viewingVersion.deployment} />}

            {/* System Message */}
            <div>
              <div className="mds-text--x-small-bold" style={{
                color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: SPACING.xs
              }}>
                System Message
              </div>
              <pre className="mds-text--small-normal" style={{
                margin: 0, padding: SPACING.md,
                backgroundColor: COLORS.backgroundLight,
                border: `1px solid ${COLORS.border}`,
                borderRadius: BORDER_RADIUS.md,
                fontFamily: FONT_FAMILY,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 320, overflowY: 'auto', color: COLORS.text
              }}>
                {viewingVersion.system_message}
              </pre>
            </div>

            {/* JSON Schema */}
            <McCodePreview
              heading="JSON Schema"
              variant="none"
              maxheight="260px"
              code={[{ language: 'json', template: JSON.stringify(viewingVersion.response_format, null, 2) }]}
            />
          </div>

          {/* Modal footer */}
          <div slot="footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING.md, width: '100%' }}>
            <MaerskButton variant="outline" onClick={() => setViewingVersion(null)}>
              Close
            </MaerskButton>
            <MaerskButton
              onClick={() => { handleRestoreVersion(viewingVersion); setViewingVersion(null); }}
            >
              Restore this version
            </MaerskButton>
          </div>
        </McModal>
      )}
    </div>
    </>
  );
};

export default SystemMessageEditor;
